import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    Calendar,
    User,
    CreditCard,
    CheckCircle,
    XCircle,
    Edit,
    RefreshCw,
    DollarSign,
    Mail,
    Phone,
    Clock,
    AlertCircle
} from 'lucide-react';
import { useGetAllMembershipsQuery, UserMembership } from '../../store/slices/apiSlice';
import { toast } from 'react-hot-toast';
import { Button } from '../../components/ui/Button';

const ViewMembership: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const membershipId = id ? parseInt(id) : null;
    const isValidId = membershipId && !isNaN(membershipId) && membershipId > 0;

    // Fetch all memberships and find the one we need
    const { data: membershipsResponse, isLoading, error, refetch } = useGetAllMembershipsQuery({
        page: 1,
        limit: 1000, // Get all to find the specific membership
    }, {
        skip: !isValidId,
    });

    const memberships = membershipsResponse?.data?.memberships || [];
    const membership = memberships.find((m: UserMembership) => m.membership_id === membershipId);

    // Get status color
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active':
                return 'bg-green-100 text-green-800';
            case 'expired':
                return 'bg-gray-100 text-gray-800';
            case 'cancelled':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    // Get status icon
    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'active':
                return <CheckCircle className="h-5 w-5 text-green-600" />;
            case 'expired':
                return <XCircle className="h-5 w-5 text-gray-600" />;
            case 'cancelled':
                return <XCircle className="h-5 w-5 text-red-600" />;
            default:
                return <XCircle className="h-5 w-5 text-gray-600" />;
        }
    };

    // Format date
    const formatDate = (dateString: string | Date | null | undefined) => formatDateUtil(dateString);;

    // Format currency
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(amount);
    };

    // Generate installment schedule
    const installmentSchedule = useMemo(() => {
        if (!membership || !membership.total_installments || membership.payment_type === 'full_payment') {
            return [];
        }

        const totalInstallments = membership.total_installments;
        const installmentsPaid = membership.installments_paid || 0;
        const startDate = new Date(membership.start_date);
        const planPrice = membership.plan?.price || 0;
        const installmentAmount = planPrice / totalInstallments;

        const schedule = [];
        
        for (let i = 0; i < totalInstallments; i++) {
            const dueDate = new Date(startDate);
            dueDate.setMonth(startDate.getMonth() + i);
            
            const isPaid = i < installmentsPaid;
            const isCurrent = i === installmentsPaid;
            const isOverdue = !isPaid && new Date() > dueDate;

            schedule.push({
                installmentNumber: i + 1,
                dueDate: dueDate,
                amount: installmentAmount,
                status: isPaid ? 'paid' : isOverdue ? 'overdue' : isCurrent ? 'current' : 'pending',
                paymentDate: isPaid ? dueDate : null, // In real scenario, this would come from payment records
            });
        }

        return schedule;
    }, [membership]);

    // Get status badge for installment
    const getInstallmentStatusBadge = (status: string) => {
        switch (status) {
            case 'paid':
                return (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Paid
                    </span>
                );
            case 'overdue':
                return (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Overdue
                    </span>
                );
            case 'current':
                return (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                        <Clock className="h-3 w-3 mr-1" />
                        Current
                    </span>
                );
            default:
                return (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
                        <Clock className="h-3 w-3 mr-1" />
                        Pending
                    </span>
                );
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading membership details...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Membership</h2>
                    <p className="text-gray-600 mb-6">
                        {(error as any)?.data?.message || 'There was an error loading the membership details. Please try again.'}
                    </p>
                    <div className="space-x-4">
                        <button
                            onClick={() => refetch()}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Retry
                        </button>
                        <button
                            onClick={() => navigate('/admin/memberships')}
                            className="text-blue-600 hover:text-blue-800"
                        >
                            ← Back to Memberships
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!membership) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Membership Not Found</h2>
                    <p className="text-gray-600 mb-6">The membership you're looking for doesn't exist.</p>
                    <button
                        onClick={() => navigate('/admin/memberships')}
                        className="text-blue-600 hover:text-blue-800"
                    >
                        ← Back to Memberships
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/admin/memberships')}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="h-5 w-5" />
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Membership Details</h1>
                                <p className="text-gray-600">Membership ID: #{membership.membership_id}</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={() => refetch()}
                                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-2"
                            >
                                <RefreshCw className="h-4 w-4" />
                                <span>Refresh</span>
                            </button>
                            <button
                                onClick={() => navigate(`/admin/memberships/edit/${membership.membership_id}`)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                            >
                                <Edit className="h-4 w-4" />
                                <span>Edit</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Membership Information */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="p-6 border-b border-gray-200 bg-white">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        {getStatusIcon(membership.status)}
                                        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(membership.status)}`}>
                                            {membership.status.toUpperCase()}
                                        </span>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm text-gray-600">Membership ID</p>
                                        <p className="text-lg font-bold text-gray-900">#{membership.membership_id}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="p-6">
                                <h2 className="text-lg font-semibold text-gray-900 mb-4">Membership Information</h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="flex items-start space-x-3">
                                        <div className="p-2 bg-blue-100 rounded-lg">
                                            <Calendar className="h-5 w-5 text-blue-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Start Date</p>
                                            <p className="text-base font-medium text-gray-900">{formatDate(membership.start_date)}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <div className="p-2 bg-blue-100 rounded-lg">
                                            <Calendar className="h-5 w-5 text-blue-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">End Date</p>
                                            <p className="text-base font-medium text-gray-900">{formatDate(membership.end_date)}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <div className="p-2 bg-blue-100 rounded-lg">
                                            <CreditCard className="h-5 w-5 text-blue-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Payment Type</p>
                                            <p className="text-base font-medium text-gray-900 capitalize">
                                                {membership.payment_type?.replace('_', ' ') || 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                    {membership.total_installments && (
                                        <div className="flex items-start space-x-3">
                                            <div className="p-2 bg-blue-100 rounded-lg">
                                                <DollarSign className="h-5 w-5 text-blue-600" />
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-600">Installments</p>
                                                <p className="text-base font-medium text-gray-900">
                                                    {membership.installments_paid || 0} / {membership.total_installments}
                                                </p>
                                                {membership.next_installment_date && (
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        Next: {formatDate(membership.next_installment_date)}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* User Information */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="p-6 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900">User Information</h2>
                            </div>
                            <div className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="flex items-start space-x-3">
                                        <div className="p-2 bg-green-100 rounded-lg">
                                            <User className="h-5 w-5 text-green-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Name</p>
                                            <p className="text-base font-medium text-gray-900">{membership.user?.name || 'N/A'}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <div className="p-2 bg-green-100 rounded-lg">
                                            <Mail className="h-5 w-5 text-green-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Email</p>
                                            <p className="text-base font-medium text-gray-900">{membership.user?.email || 'N/A'}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <div className="p-2 bg-green-100 rounded-lg">
                                            <User className="h-5 w-5 text-green-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">User ID</p>
                                            <p className="text-base font-medium text-gray-900">#{membership.user?.user_id || 'N/A'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Timeline */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="p-6 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900">Timeline</h2>
                            </div>
                            <div className="p-6">
                                <div className="space-y-4">
                                    <div className="flex items-start space-x-3">
                                        <div className="p-2 bg-blue-100 rounded-full">
                                            <Calendar className="h-4 w-4 text-blue-600" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-gray-900">Membership Created</p>
                                            <p className="text-xs text-gray-600">
                                                {formatDateTimeUtil(membership.created_at)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Plan Information */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="p-6 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900">Plan Information</h2>
                            </div>
                            <div className="p-6">
                                <div className="space-y-4">
                                    <div className="flex items-start space-x-3">
                                        <div className="p-2 bg-purple-100 rounded-lg">
                                            <CreditCard className="h-5 w-5 text-purple-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Plan Name</p>
                                            <p className="text-base font-medium text-gray-900">{membership.plan?.name || 'N/A'}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <div className="p-2 bg-purple-100 rounded-lg">
                                            <DollarSign className="h-5 w-5 text-purple-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Plan Price</p>
                                            <p className="text-base font-medium text-gray-900">
                                                {membership.plan?.price ? formatCurrency(membership.plan.price) : 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <div className="p-2 bg-purple-100 rounded-lg">
                                            <CreditCard className="h-5 w-5 text-purple-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Plan ID</p>
                                            <p className="text-base font-medium text-gray-900">#{membership.plan?.plan_id || 'N/A'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Monthly Installment Tracking Table - Full Width */}
                {membership.payment_type !== 'full_payment' && installmentSchedule.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900">Monthly Installment Tracking</h2>
                                    <p className="text-sm text-gray-600 mt-1">
                                        Track payment schedule and status for all installments
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm text-gray-600">Progress</p>
                                    <p className="text-2xl font-bold text-blue-600">
                                        {membership.installments_paid || 0}/{membership.total_installments}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Installment #
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Due Date
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Amount
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Payment Date
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {installmentSchedule.map((installment, index) => (
                                        <tr 
                                            key={index}
                                            className="transition-colors hover:bg-gray-50"
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        #{installment.installmentNumber}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center space-x-2">
                                                    <Calendar className="h-4 w-4 text-gray-400" />
                                                    <span className="text-sm text-gray-900">
                                                        {formatDate(installment.dueDate.toISOString())}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center space-x-2">
                                                    <DollarSign className="h-4 w-4 text-gray-400" />
                                                    <span className="text-sm font-semibold text-gray-900">
                                                        {formatCurrency(installment.amount)}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {getInstallmentStatusBadge(installment.status)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {installment.paymentDate ? (
                                                    <div className="flex items-center space-x-2">
                                                        <CheckCircle className="h-4 w-4 text-green-500" />
                                                        <span className="text-sm text-gray-900">
                                                            {formatDate(installment.paymentDate.toISOString())}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-sm text-gray-400">-</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {/* Summary Footer */}
                        <div className="p-6 bg-gray-50 border-t border-gray-200">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="bg-white p-4 rounded-lg border border-gray-200">
                                    <p className="text-xs text-gray-600 mb-1">Total Amount</p>
                                    <p className="text-lg font-bold text-gray-900">
                                        {formatCurrency(membership.plan?.price || 0)}
                                    </p>
                                </div>
                                <div className="bg-white p-4 rounded-lg border border-gray-200">
                                    <p className="text-xs text-gray-600 mb-1">Amount Paid</p>
                                    <p className="text-lg font-bold text-green-600">
                                        {formatCurrency(
                                            (membership.plan?.price || 0) / (membership.total_installments || 1) * (membership.installments_paid || 0)
                                        )}
                                    </p>
                                </div>
                                <div className="bg-white p-4 rounded-lg border border-gray-200">
                                    <p className="text-xs text-gray-600 mb-1">Amount Remaining</p>
                                    <p className="text-lg font-bold text-orange-600">
                                        {formatCurrency(
                                            (membership.plan?.price || 0) - 
                                            ((membership.plan?.price || 0) / (membership.total_installments || 1) * (membership.installments_paid || 0))
                                        )}
                                    </p>
                                </div>
                                <div className="bg-white p-4 rounded-lg border border-gray-200">
                                    <p className="text-xs text-gray-600 mb-1">Per Installment</p>
                                    <p className="text-lg font-bold text-blue-600">
                                        {formatCurrency(
                                            (membership.plan?.price || 0) / (membership.total_installments || 1)
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ViewMembership;

